/**
 * @fileoverview Profile popup menu anchored to the sidebar profile button.
 *
 * Renders a dropdown above the profile button containing:
 *   - User identity (avatar, name, email, tier badge)
 *   - Partner status (linked/unlinked)
 *   - Menu items: Manage subscription, Partner, Account & settings,
 *     Help & support, Sign out
 *
 * Wired actions:
 *   - sign-out: emits `profile:signout` on eventBus (handled by App.handleLogout)
 *   - partner: navigates to /partner regardless of link state — the page
 *     itself surfaces the invite CTA when unlinked and the management UI
 *     when linked
 *   - manage-subscription: Basic → /upgrade; Premium, lapsed and past-due →
 *     /subscription
 *   - account-settings: navigates to /account-settings
 *   - memory: navigates to /memory
 *   - help: navigates to /help
 *
 * @module components/ProfilePopup
 */

import { createProfileAvatar } from '../utils/profileAvatar.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import {
  fetchSubscriptionStatus as fetchSharedStatus,
  planFromStatus,
  isPaidPlan,
  planName
} from '../core/subscriptionStatus.js';
import { eventBus } from '../core/utils.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';

const POPUP_MARGIN = 8;

export class ProfilePopup {
  constructor() {
    this.popup = document.getElementById('profile-popup');
    this.anchor = document.getElementById('sidebar-profile-btn');
    this.isOpen = false;
    this.discoveryApi = new DiscoveryAPI();
    this.isPartnerLinked = false;
    /** @type {'basic'|'premium'|'lapsed'|'past_due'} Badge/routing state. */
    this.tierState = 'basic';

    // Partner cache: undefined = never resolved, null = unlinked, object = linked.
    // Used to skip the "Link partner" flash on re-opens while the fetch runs.
    this.cachedPartner = undefined;
    this.cachedUserKey = null;

    // Monotonic tokens guard against stale async responses overwriting the
    // UI after a newer update() call has started.
    this.partnerFetchToken = 0;
    this.subscriptionFetchToken = 0;

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResize = this.handleResize.bind(this);

    // Re-parent to <body> so the popup escapes the sidebar's containing block.
    // On mobile the sidebar uses transform + overflow: hidden, which would
    // otherwise clip this position:fixed menu to the sidebar's 250px width.
    if (this.popup && this.popup.parentElement !== document.body) {
      document.body.appendChild(this.popup);
    }

    if (this.popup) {
      this.popup.addEventListener('click', (e) => {
        const item = e.target.closest('.profile-popup__item');
        if (!item) return;
        this.handleAction(item.dataset.action);
      });
    }

    // Keep the dropdown's partner row in sync when the link state changes
    // elsewhere in the app (currently: PartnerSection's unlink action).
    eventBus.on('partner:unlinked', () => {
      this.cachedPartner = null;
      this.setPartnerStatus(null);
    });
  }

  /**
   * Route a menu item click to its wired handler. Unknown actions are logged
   * as stubs until they get wired.
   */
  handleAction(action) {
    eventBus.emit('sidebar:close-mobile');
    switch (action) {
      case 'sign-out':
        this.close();
        eventBus.emit('profile:signout');
        return;
      case 'partner':
        this.close();
        eventBus.emit('navigate', { page: 'partner' });
        return;
      case 'manage-subscription':
        this.close();
        this.handleManageSubscription();
        return;
      case 'account-settings':
        this.close();
        eventBus.emit('navigate', { page: 'account-settings' });
        return;
      case 'memory':
        this.close();
        eventBus.emit('navigate', { page: 'memory' });
        return;
      case 'help':
        this.close();
        eventBus.emit('navigate', { page: 'help' });
        return;
      default:
        console.log('[ProfilePopup] action (stub):', action);
        this.close();
    }
  }

  /**
   * Basic users → in-app upgrade/pricing page.
   * Premium users → in-app subscription management page (/subscription), which
   * deep-links into Stripe Customer Portal flows for specific actions.
   * Lapsed users (UNPAID/CANCELED) → /subscription for the reactivation card;
   * they have Premium history and the page offers a one-click resume.
   * Past-due users (PAST_DUE) → /subscription for the payment-issue card.
   * This is not an upgrade nudge, so it never routes through goToUpgrade.
   */
  handleManageSubscription() {
    const goSubscription = isPaidPlan(this.tierState)
      || this.tierState === 'lapsed'
      || this.tierState === 'past_due';
    if (goSubscription) {
      eventBus.emit('navigate', { page: 'subscription' });
    } else {
      // Basic users route to /upgrade — count the click via the shared contract.
      goToUpgrade(eventBus, 'profilePopup');
    }
  }

  /**
   * Populate the popup from the current user object, then fetch partner and
   * subscription status asynchronously. Safe to call multiple times — stale
   * async responses are discarded via monotonic fetch tokens.
   *
   * @param {Object} user - Current user object
   * @param {Object} [options]
   * @param {boolean} [options.skipFetch=false] - Skip async partner and
   *   subscription fetches. Used by dev tools to preview mock states
   *   without overriding them with the real logged-in user's data.
   */
  update(user, options = {}) {
    if (!this.popup || !user) return;

    // Drop partner cache if the identified user has changed.
    const userKey = user.id || user.email || null;
    if (userKey !== this.cachedUserKey) {
      this.cachedPartner = undefined;
      this.cachedUserKey = userKey;
    }

    // Avatar
    const avatarSlot = document.getElementById('profile-popup-avatar');
    if (avatarSlot) {
      avatarSlot.innerHTML = '';
      const avatar = createProfileAvatar(user, { size: 40 });
      avatarSlot.appendChild(avatar);
    }

    // Name
    const nameEl = document.getElementById('profile-popup-name');
    if (nameEl) {
      nameEl.textContent = user.firstName || user.name || user.username || 'User';
    }

    // Email
    const emailEl = document.getElementById('profile-popup-email');
    if (emailEl) {
      emailEl.textContent = user.email || '';
    }

    // Tier badge: default to BASIC, then step up if the user object's
    // subscriptionTier (the identity response's boot-time stamp, or a dev
    // override) reports a paid plan. The status fetch below corrects it.
    this.setTierBadge(isPaidPlan(user.subscriptionTier) ? user.subscriptionTier : 'basic');

    // Partner row: render from cache if we've resolved before; otherwise
    // leave the HTML default ("Partner" label, meta hidden) until the fetch
    // resolves. This avoids a brief "Link partner" flash for linked users.
    if (this.cachedPartner !== undefined) {
      this.setPartnerStatus(this.cachedPartner);
    }

    if (options.skipFetch) {
      // Dev-mode preview: caller has already set subscriptionTier on the mock
      // user; honour it and skip the network calls. Partner stays hidden
      // unless the caller explicitly provides a partner via setPartnerStatus().
      return;
    }

    this.fetchPartner();
    this.fetchSubscriptionStatus();
  }

  /**
   * Fetch the current user's partner from the backend and render the row.
   * Token-guarded to avoid stale overwrites.
   */
  async fetchPartner() {
    const token = ++this.partnerFetchToken;
    try {
      const result = await this.discoveryApi.getPartner();
      if (token !== this.partnerFetchToken) return; // stale
      const partner = result?.data || null;
      this.cachedPartner = partner;
      this.setPartnerStatus(partner);
    } catch (err) {
      if (token !== this.partnerFetchToken) return;
      console.warn('[ProfilePopup] Failed to fetch partner:', err?.error || err);
      // On first-time error, fall back to unlinked so the row stays actionable.
      // Otherwise preserve the last known good state.
      if (this.cachedPartner === undefined) {
        this.setPartnerStatus(null);
      }
    }
  }

  /**
   * Read the shared subscription snapshot and set the tier badge accordingly.
   * Token-guarded so a response for a previous user can't repaint the badge.
   */
  async fetchSubscriptionStatus() {
    const token = ++this.subscriptionFetchToken;
    const status = await fetchSharedStatus();
    if (token !== this.subscriptionFetchToken) return; // stale
    const plan = planFromStatus(status);
    if (status?.status === 'PAST_DUE') {
      // Payment failed but the grace period keeps the plan entitled, so the
      // payload still reads as premium — the payment issue takes precedence
      // over the generic paid-plan badge.
      this.setTierBadge('past_due');
    } else if (isPaidPlan(plan)) {
      this.setTierBadge(plan);
    } else if (status?.status === 'UNPAID' || status?.status === 'CANCELED') {
      this.setTierBadge('lapsed');
    } else {
      this.setTierBadge('basic');
    }
  }

  /**
   * Render the tier badge in the given variant ('basic' | 'premium' |
   * 'lapsed' | 'past_due'), track the state for click-handling, and swap the
   * subscription menu-item label. Lapsed users (UNPAID/CANCELED) keep the BASIC
   * badge but see "Reactivate subscription" so the dropdown routes them to the
   * in-place reactivation card on /subscription rather than /upgrade. Past-due
   * users (PAST_DUE — payment failed, in grace) swap the PREMIUM badge for
   * "PAYMENT ISSUE" and see "Fix payment issue", routing to /subscription's
   * payment-issue card.
   *
   * @param {'basic'|'premium'|'lapsed'|'past_due'} tier - Badge variant
   */
  setTierBadge(tier) {
    const isPaid = isPaidPlan(tier);
    const isLapsed = tier === 'lapsed';
    const isPastDue = tier === 'past_due';
    this.tierState = tier;

    const badgeEl = document.getElementById('profile-popup-badge');
    if (badgeEl) {
      badgeEl.textContent = isPastDue
        ? 'PAYMENT ISSUE'
        : `${planName(isPaid ? tier : 'basic').toUpperCase()} PLAN`;
      badgeEl.classList.toggle('profile-popup__badge--premium', isPaid);
      badgeEl.classList.toggle('profile-popup__badge--free', !isPaid && !isPastDue);
      badgeEl.classList.toggle('profile-popup__badge--payment-issue', isPastDue);
      badgeEl.hidden = false;
    }

    const subLabelEl = document.getElementById('profile-popup-subscription-label');
    if (subLabelEl) {
      if (isPastDue) subLabelEl.textContent = 'Fix payment issue';
      else if (isPaid) subLabelEl.textContent = 'Manage subscription';
      else if (isLapsed) subLabelEl.textContent = 'Reactivate subscription';
      else subLabelEl.textContent = 'Upgrade plan';
    }
  }

  /**
   * Render the partner row. Two states only:
   *   linked   → label "Partner", green "• Name" meta
   *   unlinked → label "Link partner", meta hidden
   */
  setPartnerStatus(partner) {
    const metaEl = document.getElementById('profile-popup-partner-meta');
    const nameEl = document.getElementById('profile-popup-partner-name');
    const labelEl = document.getElementById('profile-popup-partner-label');
    if (!metaEl || !nameEl || !labelEl) return;

    const linked = !!(partner && (partner.firstName || partner.name || partner.username));
    this.isPartnerLinked = linked;

    if (linked) {
      nameEl.textContent = partner.firstName || partner.name || partner.username;
      metaEl.hidden = false;
      labelEl.textContent = 'Partner';
    } else {
      nameEl.textContent = '';
      metaEl.hidden = true;
      labelEl.textContent = 'Link partner';
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.popup || !this.anchor || this.isOpen) return;

    this.popup.hidden = false;
    this.position();
    this.isOpen = true;

    // Defer listener binding so the triggering click doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener('mousedown', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('scroll', this.handleResize, true);
    }, 0);
  }

  close() {
    if (!this.popup || !this.isOpen) return;

    this.popup.hidden = true;
    this.isOpen = false;

    document.removeEventListener('mousedown', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleResize, true);
  }

  /**
   * Position the popup just above the profile button using fixed coords.
   * Works in both collapsed and pinned sidebar states.
   */
  position() {
    if (!this.popup || !this.anchor) return;

    const anchorRect = this.anchor.getBoundingClientRect();
    const popupRect = this.popup.getBoundingClientRect();

    let top = anchorRect.top - popupRect.height - POPUP_MARGIN;
    let left = anchorRect.left;

    // Keep popup within viewport horizontally
    const viewportWidth = window.innerWidth;
    if (left + popupRect.width > viewportWidth - 8) {
      left = viewportWidth - popupRect.width - 8;
    }
    if (left < 8) {
      left = 8;
    }

    // Clamp both vertical edges. The popup opens upward off the profile button,
    // so the bottom clamp only bites in the short-viewport case (phone in
    // landscape) — but the mobile step-up makes the popup taller, so it can.
    // Top clamp runs last: a popup taller than the viewport pins to the top.
    top = Math.min(top, window.innerHeight - popupRect.height - 8);
    top = Math.max(8, top);

    this.popup.style.top = `${top}px`;
    this.popup.style.left = `${left}px`;
  }

  handleOutsideClick(e) {
    if (!this.popup) return;
    if (this.popup.contains(e.target)) return;
    if (this.anchor && this.anchor.contains(e.target)) return;
    this.close();
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      if (this.anchor) this.anchor.focus();
    }
  }

  handleResize() {
    if (this.isOpen) this.position();
  }
}
